import React, { VFC, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import p3Img1 from '../../assets/images/p3/1.png';
import p3Img2 from '../../assets/images/p3/2.png';
import p3Img3 from '../../assets/images/p3/3.png';
import p3Img4 from '../../assets/images/p3/4.png';
import p3Img5 from '../../assets/images/p3/5.png';
import p3Img6 from '../../assets/images/p3/6.png';
import p3Img7 from '../../assets/images/p3/7.png';
import iphoneImg from '../../assets/images/p3/iphone.png';
import videoCover from '../../assets/images/video-cover.png';
import p3Video from '../../assets/video/p3-1.mp4';
import { useBgControl } from '../BgControlContext';
import type { BgPalette } from '../BgControlContext';

type Item = { name: string; image: string; palette: BgPalette };

const items: Item[] = [
  { name: '房产', image: p3Img1, palette: ['#FF6B35', '#F6AE2D', '#0F4C5C'] },
  { name: '出版', image: p3Img2, palette: ['#FF5F6D', '#A8C0FF', '#2B3A67'] },
  { name: '电商', image: p3Img3, palette: ['#FF8E72', '#56CFE1', '#1D2D50'] },
  { name: '客服', image: p3Img4, palette: ['#4facfe', '#00f2fe', '#1D2D50'] },
  { name: '自动化', image: p3Img5, palette: ['#43e97b', '#38f9d7', '#1D2D50'] },
  { name: '文旅', image: p3Img6, palette: ['#F98F39', '#9AD0EC', '#2E4A62'] },
  { name: '咨询', image: p3Img7, palette: ['#a18cd1', '#fbc2eb', '#2B3A67'] },
];

const PhoneVideoPlayer: VFC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className={styles.phoneContainer} style={{ pointerEvents: 'auto' }} onClick={togglePlay}>
      <video
        ref={videoRef}
        src={p3Video}
        poster={videoCover}
        loop
        muted
        playsInline
        preload="metadata"
        className={styles.phoneVideo}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <img src={iphoneImg} alt="" className={styles.phoneFrame} />

      {!isPlaying && (
        <div className={styles.playButton}>
          <div className={styles.playIcon} />
        </div>
      )}
    </div>
  );
};

export const P3Section: VFC<{ containerRef?: React.RefObject<HTMLElement> }> = ({ containerRef }) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const internalRef = useRef<HTMLElement | null>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = containerRef || internalRef;
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const bgControl = useBgControl();
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const edgeScrollAttemptRef = useRef(0); // 记录在边界处的滚动尝试次数
  const edgeResetTimeoutRef = useRef<number | null>(null); // 边界计数器重置定时器
  const scrollReleasedRef = useRef(false); // 标记是否已释放滚动
  const inViewRef = useRef(false); // 使用ref存储inView的最新值

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const slides = useMemo(() => {
    const baseSlides = items.map((it, idx) => ({
      id: idx,
      name: it.name,
      image: it.image,
      palette: it.palette,
    }));

    // 移动端复制slides以确保循环滚动流畅
    if (isMobile) {
      const repeated = [...baseSlides, ...baseSlides, ...baseSlides];
      // 重新分配id以确保唯一性
      return repeated.map((slide, idx) => ({
        ...slide,
        id: idx,
      }));
    }

    return baseSlides;
  }, [isMobile]);

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isInView = entry.isIntersecting;
        setInView(isInView);
        inViewRef.current = isInView; // 同步更新ref
      },
      { threshold: 0.4 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    if (!bgControl) return;
    if (inView) {
      const current = slides[active];
      if (current) {
        bgControl.setOverridePalette(current.palette);
      }
    } else {
      bgControl.setOverridePalette(null);
    }
  }, [active, inView, bgControl, slides]);

  useEffect(
    () => () => {
      if (bgControl) {
        bgControl.setOverridePalette(null);
      }
    },
    [bgControl],
  );

  // 劫持页面滚动，转换为轮播图切换
  useEffect(() => {
    if (isMobile) return; // 移动端不启用此功能

    const wrapper = viewerRef.current;
    if (!wrapper) return;

    const handleWheel = (e: WheelEvent) => {
      const currentInView = inViewRef.current;
      // 只在P3区域内且inView时生效
      if (!currentInView || !swiperRef.current) {
        return;
      }

      const swiper = swiperRef.current;
      const currentIndex = swiper.activeIndex;
      const isAtStart = currentIndex === 0;
      const isAtEnd = currentIndex === items.length - 1;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      // 判断是否需要劫持滚动
      let shouldPreventScroll = false;

      if (isScrollingDown && !isAtEnd) {
        // 向下滚动且不在最后一个
        shouldPreventScroll = true;
        edgeScrollAttemptRef.current = 0; // 重置边界滚动计数
        scrollReleasedRef.current = false; // 重置释放标志
        // 清除边界重置定时器
        if (edgeResetTimeoutRef.current) {
          window.clearTimeout(edgeResetTimeoutRef.current);
          edgeResetTimeoutRef.current = null;
        }
      } else if (isScrollingUp && !isAtStart) {
        // 向上滚动且不在第一个
        shouldPreventScroll = true;
        edgeScrollAttemptRef.current = 0; // 重置边界滚动计数
        scrollReleasedRef.current = false; // 重置释放标志
        // 清除边界重置定时器
        if (edgeResetTimeoutRef.current) {
          window.clearTimeout(edgeResetTimeoutRef.current);
          edgeResetTimeoutRef.current = null;
        }
      } else if ((isScrollingDown && isAtEnd) || (isScrollingUp && isAtStart)) {
        // 在边界处滚动
        if (scrollReleasedRef.current) {
          // 如果已经释放过滚动，不再阻止
          shouldPreventScroll = false;
        } else if (edgeScrollAttemptRef.current < 1) {
          // 第一次在边界滚动，仍然阻止
          shouldPreventScroll = true;
          edgeScrollAttemptRef.current++;
        } else {
          // 第二次在边界滚动，释放页面滚动
          shouldPreventScroll = false;
          scrollReleasedRef.current = true; // 标记已释放

          // 延迟重置所有状态
          if (edgeResetTimeoutRef.current) {
            window.clearTimeout(edgeResetTimeoutRef.current);
          }
          edgeResetTimeoutRef.current = window.setTimeout(() => {
            edgeScrollAttemptRef.current = 0;
            scrollReleasedRef.current = false;
          }, 1000);
        }
      }

      if (shouldPreventScroll) {
        e.preventDefault();
        e.stopPropagation();

        // 防抖处理，避免过快切换
        if (isScrollingRef.current) return;

        isScrollingRef.current = true;

        if (isScrollingDown && !isAtEnd) {
          swiper.slideNext();
        } else if (isScrollingUp && !isAtStart) {
          swiper.slidePrev();
        }

        // 清除之前的定时器
        if (scrollTimeoutRef.current) {
          window.clearTimeout(scrollTimeoutRef.current);
        }

        // 设置新的定时器，解除滚动锁定
        scrollTimeoutRef.current = window.setTimeout(() => {
          isScrollingRef.current = false;
        }, 650); // 稍微长一点，确保动画完成
      }
    };

    // 使用passive: false允许preventDefault
    wrapper.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      wrapper.removeEventListener('wheel', handleWheel);
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      if (edgeResetTimeoutRef.current) {
        window.clearTimeout(edgeResetTimeoutRef.current);
      }
    };
  }, [isMobile]); // 只依赖isMobile，避免重复绑定

  return (
    <section ref={internalRef} className={styles.wrapper} aria-label="P3">
      <h2 className={styles.title}>专属智能体项目</h2>
      <p className={styles.desc}>
        我们已在电商、出版、医疗、房产等多个行业，交付了深入业务的专属智能体项目。
      </p>
      <div className={styles.row}>
        <ul className={styles.side}>
          <div className={styles.rail} />
          {items.map((it, i) => (
            <li
              key={it.name}
              className={i === active ? styles.sideItemActive : styles.sideItem}
              onClick={() => swiperRef.current?.slideTo(i)}
            >
              {it.name}
            </li>
          ))}
        </ul>
        <div className={styles.viewer} ref={viewerRef}>
          <Swiper
            direction={isMobile ? 'horizontal' : 'vertical'}
            slidesPerView={isMobile ? 'auto' : 1}
            centeredSlides={false}
            spaceBetween={isMobile ? 10 : 20}
            loop={isMobile}
            autoplay={isMobile ? { delay: 3000, disableOnInteraction: false } : false}
            speed={600}
            modules={isMobile ? [Autoplay] : []}
            onSwiper={(s: SwiperType) => {
              swiperRef.current = s;
            }}
            onSlideChange={(s: SwiperType) => {
              // 移动端slides被复制了3次，需要取模获取真实索引
              const realIndex = isMobile ? s.activeIndex % items.length : s.activeIndex;
              setActive(realIndex);
            }}
            className={styles.swiper}
          >
            {slides.map((s, idx) => (
              <SwiperSlide key={`slide-${idx}`} className={styles.slide}>
                <div className={styles.slideBox}>
                  <img src={s.image} alt={s.name} className={styles.slideImage} />
                  {s.name === '房产' && (
                    <div className={styles.phoneLayer}>
                      <PhoneVideoPlayer />
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

const phoneContainerStyle = css`
  position: relative;
  height: 85%;
  width: auto;
  aspect-ratio: 350 / 700;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    height: 70%;
  }
`;

const playButtonStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 58px !important;
  height: 58px !important;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  backdrop-filter: blur(4px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    width: 36px !important;
    height: 36px !important;
  }
`;

const styles = {
  wrapper: css`
    pointer-events: none;
    position: relative;
    min-height: calc(var(--vh, 1vh) * 100);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 6vh 0;
    width: min(1400px, 92vw);
    color: #fff;
  `,
  title: css`
    pointer-events: auto;
    text-align: center;
    font-size: 42px;
    font-weight: 800;
    margin: 0 0 5px 0;
    opacity: 0;
    transform: translate3d(30px, 0, 0);
    transition: opacity 0.7s ease, transform 0.7s ease;
    .reveal-visible & {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
    @media (max-width: 768px) {
      font-size: 32px;
    }
  `,
  desc: css`
    pointer-events: auto;
    text-align: center;
    margin: 0 auto 60px;
    max-width: 760px;
    font-size: 20px;
    opacity: 0;
    transform: translate3d(-30px, 0, 0);
    transition: opacity 0.7s ease, transform 0.7s ease;
    .reveal-visible & {
      opacity: 0.9;
      transform: translate3d(0, 0, 0);
    }
    @media (max-width: 768px) {
      font-size: 15px;
      margin-bottom: 30px;
    }
  `,
  row: css`
    pointer-events: auto;
    display: grid;
    grid-template-columns: 120px 1fr;
    align-items: stretch;
    gap: 18px;
    opacity: 0;
    transition: opacity 0.7s ease 0.1s;

    .reveal-visible & {
      opacity: 1;
    }
    
    @media (max-width: 768px) {
      display: flex;
      flex-direction: column-reverse;
      gap: 20px;
    }
  `,
  side: css`
    position: relative;
    margin: 0;
    padding: 30px 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    
    @media (max-width: 768px) {
      flex-direction: row;
      justify-content: center;
      gap: 20px;
      padding: 0;
      height: auto;
      width: 100%;
      flex-wrap: wrap;
    }
  `,
  rail: css`
    position: absolute;
    left: 14px;
    top: 0;
    bottom: 0;
    border-left: 1px dashed rgba(255, 255, 255, 0.35);
    opacity: 0.8;
    
    @media (max-width: 768px) {
      display: none;
    }
  `,
  sideItem: css`
    padding-left: 26px;
    opacity: 0.75;
    cursor: pointer;
    user-select: none;
    transition: opacity 0.2s linear;
    position: relative;
    &:hover {
      opacity: 1;
    }
    &::before {
      content: '';
      position: absolute;
      left: 12.4px;
      top: 50%;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      transform: translateY(-50%);
    }
    
    @media (max-width: 768px) {
      display: none;
    }
  `,
  sideItemActive: css`
    padding-left: 26px;
    font-weight: 700;
    position: relative;
    opacity: 1;
    &::before {
      content: '';
      position: absolute;
      left: 10.7px;
      top: 50%;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #fff;
      transform: translateY(-50%);
    }
    
    @media (max-width: 768px) {
      padding-left: 0;
      font-size: 16px;
      color: #fff;
      text-align: center;
      width: 100%;
      &::before {
        display: none;
      }
    }
  `,
  viewer: css`
    position: relative;
    background: rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(6px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    height: clamp(320px, calc(var(--vh, 1vh) * 75), 720px);
    
    @media (max-width: 768px) {
      height: auto;
      overflow: visible;
    }
  `,
  swiper: css`
    height: 100%;
    width: 100%;
  `,
  slide: css`
    height: 100% !important;
    display: flex;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
      width: 90% !important;
      aspect-ratio: 16 / 9;
      height: auto !important;
    }
  `,
  slideBox: css`
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
  `,
  slideImage: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  `,
  phoneLayer: css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    pointer-events: none;
    transform: translateX(10%);
    
    @media (max-width: 768px) {
      width: 45%;
      transform: none;
      justify-content: flex-end;
      padding-right: 5%;
    }
  `,
  phoneContainer: phoneContainerStyle,
  phoneFrame: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    z-index: 2;
  `,
  phoneVideo: css`
    position: absolute;
    top: 2.3%; 
    left: 6%;
    width: 88.5%;
    height: 95.5%; 
    object-fit: cover;
    border-radius: 24px;
    z-index: 1;
    background: #000;
  `,
  playButton: playButtonStyle,
  playIcon: css`
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 11px 0 11px 18px;
    border-color: transparent transparent transparent #fff;
    margin-left: 6px;

    @media (max-width: 768px) {
      border-width: 7px 0 7px 12px;
      margin-left: 3px;
    }
  `,
};

